// MCHD Room Scheduler - Coordinator API Endpoints
// Created: 07-10-2025 14:01:32
// Purpose: API client functions for coordinator dashboard data

import { api } from '../index';

/**
 * Coordinator Dashboard KPIs Interface
 */
export interface CoordinatorDashboardKPIs {
  utilization: number;
  vacantSessions: number;
  blockedHours: number;
  conflicts: number;
  publishedCoverage: number;
}

/**
 * API Response wrapper interface
 */
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  timestamp?: string;
}

/**
 * Get coordinator dashboard KPIs
 * 
 * @param params - Query parameters for filtering
 * @param params.startDate - Start date (ISO format)
 * @param params.endDate - End date (ISO format)
 * @param params.buildingId - Building ID filter
 * @returns Promise with KPIs data
 * 
 * Created: 07-10-2025 14:01:32
 */
export const getCoordinatorKPIs = async (params?: {
  startDate?: string;
  endDate?: string;
  buildingId?: string;
}): Promise<ApiResponse<CoordinatorDashboardKPIs>> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.buildingId) queryParams.append('buildingId', params.buildingId);

    const queryString = queryParams.toString();
    const url = `/dashboards/coordinator/kpis${queryString ? `?${queryString}` : ''}`;
    
    // api.get() returns JSON directly: { success: true, data: {...}, meta: {...} }
    const response = await api.get(url);
    return response;
  } catch (error) {
    console.error('[Coordinator API] Error fetching KPIs:', error);
    throw error;
  }
};

/**
 * Get today's room states
 * 
 * @param params - Query parameters
 * @returns Promise with room states
 * 
 * Created: 07-10-2025 14:01:32
 */
export const getRoomStates = async (params?: {
  date?: string;
  buildingId?: string;
  floorId?: string;
}): Promise<ApiResponse<any[]>> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.date) queryParams.append('date', params.date);
    if (params?.buildingId) queryParams.append('buildingId', params.buildingId);
    if (params?.floorId) queryParams.append('floorId', params.floorId);

    const queryString = queryParams.toString();
    const url = `/dashboards/coordinator/room-states${queryString ? `?${queryString}` : ''}`;
    
    // api.get() returns JSON directly: { success: true, data: [...], meta: {...} }
    const response = await api.get(url);
    return response;
  } catch (error) {
    console.error('[Coordinator API] Error fetching room states:', error);
    throw error;
  }
};

/**
 * Get upcoming blocks
 * 
 * @param params - Query parameters
 * @returns Promise with upcoming blocks
 * 
 * Created: 07-10-2025 14:01:32
 */
export const getUpcomingBlocks = async (params?: {
  startDate?: string;
  endDate?: string;
  buildingId?: string;
  floorId?: string;
}): Promise<ApiResponse<any[]>> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.buildingId) queryParams.append('buildingId', params.buildingId);
    if (params?.floorId) queryParams.append('floorId', params.floorId);

    const queryString = queryParams.toString();
    const url = `/dashboards/coordinator/upcoming-blocks${queryString ? `?${queryString}` : ''}`;
    
    // api.get() returns JSON directly: { success: true, data: [...], meta: {...} }
    const response = await api.get(url);
    return response;
  } catch (error) {
    console.error('[Coordinator API] Error fetching upcoming blocks:', error);
    throw error;
  }
};

/**
 * Get conflicts
 * 
 * @param params - Query parameters
 * @returns Promise with conflicts data
 * 
 * Created: 07-10-2025 14:01:32
 */
export const getConflicts = async (params?: {
  startDate?: string;
  endDate?: string;
  buildingId?: string;
  floorId?: string;
  roomId?: string;
}): Promise<ApiResponse<any[]>> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.buildingId) queryParams.append('buildingId', params.buildingId);
    if (params?.floorId) queryParams.append('floorId', params.floorId);
    if (params?.roomId) queryParams.append('roomId', params.roomId);

    const queryString = queryParams.toString();
    const url = `/dashboards/coordinator/conflicts${queryString ? `?${queryString}` : ''}`;
    
    // api.get() returns JSON directly: { success: true, data: [...], meta: {...} }
    const response = await api.get(url);
    return response;
  } catch (error) {
    console.error('[Coordinator API] Error fetching conflicts:', error);
    throw error;
  }
};

/**
 * Get utilization data
 * 
 * @param params - Query parameters
 * @returns Promise with utilization data
 * 
 * Created: 07-10-2025 14:01:32
 */
export const getUtilizationData = async (params?: {
  startDate?: string;
  endDate?: string;
  buildingId?: string;
  floorId?: string;
  groupBy?: string;
}): Promise<ApiResponse<{ byRoom: any[]; byTime: any[] }>> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.buildingId) queryParams.append('buildingId', params.buildingId);
    if (params?.floorId) queryParams.append('floorId', params.floorId);
    if (params?.groupBy) queryParams.append('groupBy', params.groupBy);

    const queryString = queryParams.toString();
    const url = `/dashboards/coordinator/utilization${queryString ? `?${queryString}` : ''}`;
    
    // api.get() returns JSON directly: { success: true, data: {...}, meta: {...} }
    const response = await api.get(url);
    return response;
  } catch (error) {
    console.error('[Coordinator API] Error fetching utilization data:', error);
    throw error;
  }
};

/**
 * Get assignment completeness
 * 
 * @param params - Query parameters
 * @returns Promise with assignment completeness data
 * 
 * Created: 07-10-2025 14:01:32
 */
export const getAssignmentCompleteness = async (params?: {
  startDate?: string;
  endDate?: string;
  buildingId?: string;
  floorId?: string;
}): Promise<ApiResponse<{
  total: number;
  completed: number;
  pending: number;
  conflicts: number;
  completeness: number;
}>> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.buildingId) queryParams.append('buildingId', params.buildingId);
    if (params?.floorId) queryParams.append('floorId', params.floorId);

    const queryString = queryParams.toString();
    const url = `/dashboards/coordinator/assignment-completeness${queryString ? `?${queryString}` : ''}`;
    
    // api.get() returns JSON directly: { success: true, data: {...}, meta: {...} }
    const response = await api.get(url);
    return response;
  } catch (error) {
    console.error('[Coordinator API] Error fetching assignment completeness:', error);
    throw error;
  }
};
